import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchAmostrasDoAlvo,
  fetchIncidente,
  fetchIncidentes,
  fetchIncidentesDoAlvo,
  fetchJobs,
  fetchMonitoramentoConfig,
  fetchMonitoramentoStatus,
  fetchOperacoesDoAlvo,
  fetchResumoJobs,
  fetchSerieAgregada,
  fetchSerieAlvo,
  registrarAckIncidente,
  type EstadoIncidente,
  type TipoJob,
} from '../api/monitoramento';
import { queryKeys } from '../lib/pwa/queryConfig';

// Cache persistido — quando o autenticador cai, queremos que o último valor
// conhecido continue visível. gcTime alto + persistência via PWA garante isso.
const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const GC_MONITORAMENTO = 7 * DAY;

export function useMonitoramentoStatus(refetchInterval: number | false = 30_000) {
  return useQuery({
    queryKey: queryKeys.monitoramento.status,
    queryFn: fetchMonitoramentoStatus,
    refetchInterval,
    gcTime: GC_MONITORAMENTO,
  });
}

export function useSerieAlvo(alvo: string | null, janela = '7d', enabled = true) {
  return useQuery({
    queryKey: queryKeys.monitoramento.serie(alvo ?? '', janela),
    queryFn: () => fetchSerieAlvo(alvo!, { bucket: 'hora', janela }),
    enabled: enabled && !!alvo,
    staleTime: 30_000,
    // O agregador horário do autenticador recomputa o bucket atual a cada 60s;
    // refetch a 60s mantém a barra "viva" sem disparar pull desnecessário.
    refetchInterval: 60_000,
    gcTime: GC_MONITORAMENTO,
  });
}

export function useIncidentes(estado: EstadoIncidente = 'ABERTO', refetchInterval: number | false = 30_000) {
  return useQuery({
    queryKey: queryKeys.monitoramento.incidentes(estado),
    queryFn: () => fetchIncidentes({ estado }),
    refetchInterval,
    gcTime: GC_MONITORAMENTO,
  });
}

export function useIncidente(id: number | null) {
  return useQuery({
    queryKey: queryKeys.monitoramento.incidente(id ?? 0),
    queryFn: () => fetchIncidente(id!),
    enabled: !!id,
    gcTime: GC_MONITORAMENTO,
  });
}

export function useJobs(
  filtros: { tipo?: TipoJob; status?: string; alvo?: string; page?: number; pageSize?: number } = {},
  refetchInterval: number | false = 30_000,
) {
  const tipoKey = filtros.tipo ?? 'TODOS';
  const statusKey = filtros.status ?? 'TODOS';
  const alvoKey = filtros.alvo ?? 'TODOS';
  const page = filtros.page ?? 1;
  const pageSize = filtros.pageSize ?? 20;
  return useQuery({
    // Inclui alvoKey na queryKey para cache separado por filtro de alvo —
    // sem isso, alternar entre alvos diferentes serviria resultado antigo.
    queryKey: [...queryKeys.monitoramento.jobs(tipoKey, statusKey, page, pageSize), alvoKey] as const,
    queryFn: () => fetchJobs({ ...filtros, page, pageSize }),
    refetchInterval,
    gcTime: GC_MONITORAMENTO,
    placeholderData: (prev) => prev,
  });
}

export function useOperacoesDoAlvo(alvo: string | null, janela: '1h' | '6h' | '24h' | '7d' = '1h') {
  return useQuery({
    queryKey: ['admin', 'monitoramento', 'alvo', alvo ?? '', 'ops', janela] as const,
    queryFn: () => fetchOperacoesDoAlvo(alvo!, janela),
    enabled: !!alvo,
    gcTime: GC_MONITORAMENTO,
  });
}

export function useIncidentesDoAlvo(alvo: string | null, limite = 20) {
  return useQuery({
    queryKey: ['admin', 'monitoramento', 'alvo', alvo ?? '', 'incidentes', limite] as const,
    queryFn: () => fetchIncidentesDoAlvo(alvo!, limite),
    enabled: !!alvo,
    gcTime: GC_MONITORAMENTO,
  });
}

export function useSerieAgregada(
  alvos: string[],
  janela: '1h' | '6h' | '24h' | '7d' = '24h',
  enabled = true,
) {
  // Ordena os alvos para garantir queryKey estável independente da ordem
  // em que o caller passa o array (evita refetch desnecessário).
  const chave = [...alvos].sort().join(',');
  return useQuery({
    queryKey: ['admin', 'monitoramento', 'serie-agregada', chave, janela] as const,
    queryFn: () => fetchSerieAgregada(alvos, janela),
    enabled: enabled && alvos.length > 0,
    staleTime: 30_000,
    // Mesmo motivo de useSerieAlvo: sparkline precisa acompanhar a recomputação
    // do bucket horário no autenticador (sem isso fica congelada após o mount).
    refetchInterval: 60_000,
    gcTime: GC_MONITORAMENTO,
  });
}

export function useAmostrasDoAlvo(
  alvo: string | null,
  inicio: string | null,
  fim: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['admin', 'monitoramento', 'alvo', alvo ?? '', 'amostras', inicio ?? '', fim ?? ''] as const,
    queryFn: () => fetchAmostrasDoAlvo(alvo!, inicio!, fim!),
    enabled: enabled && !!alvo && !!inicio && !!fim,
    // Janela passada (bucket histórico) não muda; staleTime alto evita refetch
    // ao reabrir o mesmo bucket. Bucket atual ainda re-busca quando o usuário
    // abre/fecha o dialog (queryKey muda com `fim` se chamador atualizar).
    staleTime: 5 * MINUTE,
    gcTime: GC_MONITORAMENTO,
  });
}

// Config muda raramente (só com restart do autenticador). staleTime longo
// evita refetch a cada montagem do glossário; 1 fetch por sessão é suficiente.
export function useMonitoramentoConfig() {
  return useQuery({
    queryKey: ['admin', 'monitoramento', 'config'] as const,
    queryFn: fetchMonitoramentoConfig,
    staleTime: HOUR,
    gcTime: GC_MONITORAMENTO,
  });
}

export function useResumoJobs(refetchInterval: number | false = 30_000) {
  return useQuery({
    queryKey: ['admin', 'monitoramento', 'jobs', 'resumo'] as const,
    queryFn: fetchResumoJobs,
    refetchInterval,
    gcTime: GC_MONITORAMENTO,
  });
}

export function useRegistrarAckIncidente() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => registrarAckIncidente(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'monitoramento'] });
    },
  });
}
